import { updateStepConfig } from '@pushly/models/lib/structs/journeys'
import { JourneyActionType } from '@pushly/models/lib/enums/journeys/journey-action-type'
import type { IActionStep } from '@pushly/models/lib/structs/journeys'
import type { RelativeDate } from '@pushly/models/lib/structs/relative-date'
// Reuse the same seconds→display-unit conversion the Feed data form uses (see
// src/components/feeds/feed-data-form-wrapper.tsx, which imports it from
// '@pushly/aqe/lib/components/feeds/feed-data-form'). We import from the leaner
// feed-form-helpers source module — feed-data-form re-exports this exact function
// but also pulls in the full FeedDataForm component tree, which the editor and its
// unit tests don't need.
import { calculateArticleAgeInputValue } from '@pushly/aqe/lib/components/feeds/feed-form-helpers'
import { isActionStep, JourneyNode, NodeEditorSubmitResult } from '../../types/journey-nodes'
import type { FeedDisplayData, FeedNotificationParams } from '../../types/journey-nodes'

// The feed step view-model types live in `types/journey-nodes.ts` (their canonical
// home — `SendNotificationActionConfiguration` there resolves
// SEND_FEED_NOTIFICATION to FeedNotificationParams, so declaring them here would
// be an import cycle). Re-exported so the existing
// `.../send-feed-notification-editor/helpers` import paths keep resolving.
export type { FeedNotificationParams, FeedDisplayData }

/**
 * Message surfaced when a submit is attempted without a selected feed.
 * Shown imperatively via antdStaticApi.message.
 */
export const FEED_REQUIRED_MESSAGE = 'Please select a feed before saving this step.'

/** True when a feed has been selected for the step. */
export function isFeedSelected(feedId: number | undefined | null): feedId is number {
    return typeof feedId === 'number' && !Number.isNaN(feedId)
}

/**
 * Human summary of a feed's article-age constraint for the Feed Configuration
 * card (e.g. "≤ 5 days"). Reuses `calculateArticleAgeInputValue` to convert the
 * persisted `intervalSeconds` into the feed's chosen display unit, then labels it
 * with `displayMetric`.
 *
 * When no `articleMaxAge` is configured the feed accepts articles of any age, so
 * we render a graceful phrase instead of a bogus "≤ N" figure (the underlying
 * helper would otherwise fall back to a 7-day default for a null input).
 */
export function formatArticleAgeText(articleMaxAge: RelativeDate | null | undefined): string {
    if (!articleMaxAge) {
        return 'with no age limit'
    }

    const value = calculateArticleAgeInputValue(articleMaxAge)
    return `≤ ${value} ${articleMaxAge.displayMetric}`
}

/**
 * Composes the updated feed notification step from the selected feed and the
 * inner notification editor result. Mirrors the recommended editor's submit
 * composition:
 *
 * - When the notification was edited, `notification_id` is cleared so the inline
 *   payload survives journey-wizard.tsx's dedup logic.
 * - When it was not edited, the existing `notification` / `notification_id`
 *   reference is preserved.
 *
 * The step is rebuilt immutably via `updateStepConfig` — no manual config spread.
 *
 * Returns undefined (a no-op) when no feed is selected; callers surface the
 * required-feed message and block the submit.
 *
 * When the caller supplies the selected feed's display data (`feed`), it is set
 * on the composed params as the enrichment-only `feed` object so the canvas
 * subtitle updates immediately (before mount-time enrichment re-runs). It is
 * never persisted — serialization/snapshot strip it. When omitted, any `feed`
 * already present on the step is preserved via the spread; enrichment/next load
 * still hydrates it otherwise.
 */
export function composeFeedNotificationStep(input: {
    // Any action node — not just one that already holds a feed step: the unified
    // content-source editor may compose a feed step from a node that currently
    // holds a recommendation step (configuration.type ===
    // SEND_RECOMMENDED_NOTIFICATION). The patch below re-stamps
    // configuration.type to SEND_FEED_NOTIFICATION so the resolved step's action
    // type always matches the selected source.
    node: JourneyNode<IActionStep>
    feedId: number | undefined
    innerResult: NodeEditorSubmitResult
    clearInvalid?: boolean
    feed?: FeedDisplayData
}): NodeEditorSubmitResult {
    const { node, feedId, innerResult, clearInvalid, feed } = input

    if (!isFeedSelected(feedId)) {
        return
    }

    // Assertion retained deliberately: per the `node` doc above, the incoming node
    // may still hold a RECOMMENDATION step being converted to a feed step, so
    // `configuration.type` is not yet SEND_FEED_NOTIFICATION and no
    // `isSendActionOfType` guard applies. Only `notification` / `notification_id`
    // (present on every action params shape) are read through this view.
    const existingParams = node.data.step.configuration.params as FeedNotificationParams

    const notificationEdited = !!innerResult
    let notification = existingParams.notification
    if (innerResult && isActionStep(innerResult.step)) {
        notification = (innerResult.step.configuration.params as unknown as FeedNotificationParams).notification
    }

    const newParams: FeedNotificationParams = {
        ...existingParams,
        feed_id: feedId,
        notification,
        notification_id: notificationEdited ? undefined : existingParams.notification_id,
        // Refresh the display-only feed object from the current selection when the
        // caller provided it; otherwise leave any existing one intact (spread above).
        ...(feed ? { feed } : {}),
    }

    const newStep = updateStepConfig(node.data.step, {
        // Resolve the action type to feed. A no-op when the node was already a
        // feed step; the meaningful case is converting a recommendation step in
        // the unified editor. Downstream (reducer/canvas/serialization/enrichment)
        // all key off configuration.type, so this is the single switch point.
        type: JourneyActionType.SEND_FEED_NOTIFICATION,
        params: newParams,
    } as Partial<typeof node.data.step.configuration>)

    return {
        nodeId: node.id,
        step: newStep,
        clearInvalid,
    }
}
